'use strict';

/**
 * azure-functions/shared/obligationEvaluations/cycle.cjs
 *
 * The obligation evaluation cycle (CTX-04; msr 4.5b, GH #7062;
 * docs/measurement/criteria-p4.md S-OE, the CTX-04 row, §8 Q2 A).
 *
 * ONE definition, read by three callers, so they can never disagree
 * (CLAUDE.md "Enter Once, Know Everywhere"):
 *   - measures/obligationCycle.cjs      CTX-04 at a period end
 *   - obligation-evaluations-write      what the writer accepts
 *   - src/utils/obligationEvaluations.js the browser's Tier 1 "evaluate this
 *                                       obligation" task and the catalogue's
 *                                       evaluation section
 * Pure — no I/O.
 *
 * WHAT AN EVALUATION IS (§8 Q2 A, product owner 2026-09-28). A person's
 * recorded evaluation of one enabled obligation: who (evaluatedBy), when
 * (evaluatedAt, server clock), the outcome, and the evidence it rests on. The
 * obligation evaluator's automatic run never counts — this module does not
 * read it, and compliance/obligationEvaluator.cjs is untouched.
 *
 * The outcome is a REVIEW RESULT, never a compliance status (OBL-B-002):
 *   no-change-needed  the requirement and the organisation's response to it
 *                     were looked at and nothing has to change
 *   change-needed     something has to change; the change is carried by a
 *                     finding in the findings register (FIND-B-003). The
 *                     evaluation is credited only while the finding the
 *                     SERVER raised for it exists (evaluation.findingId,
 *                     findingRaisedAt; finding.obligationEvaluationId) — a gap
 *                     recorded and sent nowhere is not an evaluation, and it
 *                     outranks any older evaluation (co-owner reviews of the
 *                     4.5b plan and diff, §11).
 * An applicability change is the SoA decision (OBL-B-001), never an outcome.
 *
 * THE CYCLE. The obligation's catalogue cadence (1m, 3m, 6m, 1y; `once` is 12
 * months, criteria-p4 CTX-04 row), shortened — never lengthened — by the
 * entry's cadenceOverride, and by the cycle stored on the evaluation itself:
 * shortening a cadence after an evaluation tightens the check at once.
 *
 * THE ANCHOR. The cycle starts at the obligation's FIRST enable, from
 * state.obligationCycleAnchors[] (server-owned; stamped by obligations-enable
 * and by state-write before an entry is removed), so disabling and re-enabling
 * never resets it. With no anchor, the entry's enabledAt. The anchor is never
 * earlier than OBLIGATION_EVALUATION_FROM: nothing could be evaluated before
 * the record existed.
 *
 * FIRST CYCLE. An obligation whose anchor is less than one cycle before the
 * period end is `excl` and shown — even when already evaluated (the row text;
 * otherwise evaluating 1 of 50 new obligations would read 100%).
 */

const DAY_MS = 86400000;

/**
 * The day sub-phase 4.5b was written, before its merge to any environment.
 * Every obligation enabled before it (all of production's) has its first
 * cycle start here — the strict side, as BIA_REVIEW_FROM (measures/bia.cjs).
 */
const OBLIGATION_EVALUATION_FROM = '2026-09-30T00:00:00.000Z';

const CYCLE_MONTHS = Object.freeze({ '1m': 1, '3m': 3, '6m': 6, '1y': 12, once: 12 });
const OUTCOMES = Object.freeze(['no-change-needed', 'change-needed']);
const EVIDENCE_TYPES = Object.freeze(['document', 'artefact']);
const MAX_EVIDENCE = 20;
const MAX_NOTE = 2000;
const MAX_VOID_REASON = 1000;
const MAX_ID = 200;
/** The Tier 1 task shows this long before the due date — a third of a short cycle. */
const DUE_SOON_MAX_DAYS = 30;

function toMs(v) {
  if (typeof v !== 'string' || !v) return NaN;
  return Date.parse(v);
}

const list = (state, key) => (state && Array.isArray(state[key]) ? state[key] : []);
const text = (v) => (typeof v === 'string' ? v.trim() : '');

/** `ms` plus `months` calendar months, UTC; the day clamps to the month's end. */
function addMonths(ms, months) {
  const d = new Date(ms);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d.getTime();
}

/** Months of a frequency, or null when it is not one this rule knows. */
function monthsOf(frequency) {
  return Object.prototype.hasOwnProperty.call(CYCLE_MONTHS, frequency) ? CYCLE_MONTHS[frequency] : null;
}

/**
 * The cycle in force, in months: the shortest of the catalogue cadence, the
 * entry's cadenceOverride and (for a stored evaluation) the cycle recorded on
 * it. A frequency this rule does not know is ignored; with no known catalogue
 * cadence the cycle is 12 months (the longest the row allows).
 */
function cycleMonths(def, entry, evaluation) {
  const candidates = [
    monthsOf(def && def.cadence && def.cadence.frequency),
    monthsOf(entry && entry.cadenceOverride && entry.cadenceOverride.frequency),
    monthsOf(evaluation && evaluation.cycle),
  ].filter((m) => m !== null);
  return candidates.length ? Math.min(...candidates) : 12;
}

/** The frequency label for `months` (what a new evaluation stores as `cycle`). */
function frequencyFor(months) {
  return Object.keys(CYCLE_MONTHS).find((k) => k !== 'once' && CYCLE_MONTHS[k] === months) || '1y';
}

/** The anchor record of one obligation, or undefined. */
function anchorOf(anchors, obligationId) {
  return (anchors || []).find((a) => a && a.obligationId === obligationId);
}

/**
 * When the obligation was first enabled: its anchor, else the entry's
 * enabledAt. → ms, or NaN.
 */
function firstEnabledMs(state, entry) {
  const a = anchorOf(list(state, 'obligationCycleAnchors'), entry && entry.obligationId);
  const fromAnchor = toMs(a && a.firstEnabledAt);
  if (!Number.isNaN(fromAnchor)) return fromAnchor;
  return toMs(entry && entry.enabledAt);
}

/** The first cycle starts at the first enable, never before the record existed. */
function cycleStartMs(firstMs) {
  const from = toMs(OBLIGATION_EVALUATION_FROM);
  return Number.isNaN(firstMs) ? from : Math.max(firstMs, from);
}

/**
 * Anchor records to append so every entry in `entries` has one. `firstOf(id)`
 * gives the earliest known enable (ms or NaN) for an obligation with none;
 * `nowIso` stands in when nothing earlier is known. The id is derived from
 * the obligation, so two writers can never add a second anchor for one.
 */
function anchorsToAdd(entries, anchors, nowIso, firstOf = () => NaN) {
  const have = new Set((anchors || []).filter((a) => a && a.obligationId).map((a) => a.obligationId));
  const out = [];
  for (const e of entries || []) {
    const id = e && e.obligationId;
    if (typeof id !== 'string' || !id || have.has(id)) continue;
    have.add(id);
    const known = [toMs(e.enabledAt), firstOf(id)].filter((t) => !Number.isNaN(t));
    const first = known.length ? new Date(Math.min(...known)).toISOString() : nowIso;
    out.push({ id: `obligation-anchor-${id}`, obligationId: id, firstEnabledAt: first, recordedAt: nowIso });
  }
  return out;
}

const isVoided = (ev) => !!(ev && ev.voidedAt);

/**
 * The finding raised for a change-needed evaluation, as the SERVER linked it:
 * the evaluation's own findingId, stamped (findingRaisedAt) on or before
 * asOfMs, naming a finding that exists and names the evaluation back
 * (obligationEvaluationId — never settable through findings-write,
 * shared/findings/obligationEvaluationLink.js). A finding a client pointed at
 * the evaluation, or one with no stamp, never counts.
 */
function findingFor(state, evaluation, asOfMs = Infinity) {
  if (!evaluation || typeof evaluation.findingId !== 'string' || !evaluation.findingId) return null;
  const at = toMs(evaluation.findingRaisedAt);
  if (Number.isNaN(at) || at > asOfMs) return null;
  return (
    list(state, 'findings').find(
      (f) => f && f.id === evaluation.findingId && f.obligationEvaluationId === evaluation.id
    ) || null
  );
}

const RETIRED = new Set(['archived', 'retired', 'deleted', 'withdrawn']);

/**
 * True when a stored evidence reference still resolves in `state` to a live
 * record — never an archived or soft-deleted one.
 */
function evidenceResolves(state, r) {
  if (!r || typeof r.id !== 'string' || !r.id) return false;
  const live = (x) =>
    x && x.id === r.id && !x.deletedAt && x.archived !== true && !RETIRED.has(x.documentStatus) && !RETIRED.has(x.status);
  if (r.type === 'document') return list(state, 'documents').some(live);
  if (r.type === 'artefact') return list(state, 'artefacts').some(live);
  return false;
}

/**
 * Why a stored evaluation earns no credit at asOfMs, or null when it does.
 * `findingsKnown` false: the findings register is not visible (the plan
 * withholds it), so a change-needed evaluation cannot be shown to be linked.
 */
function creditRefusal(state, ev, asOfMs, { findingsKnown = true } = {}) {
  if (isVoided(ev)) return 'voided';
  if (!text(ev.evaluatedBy)) return 'noEvaluator';
  if (!OUTCOMES.includes(ev.outcome)) return 'unknownOutcome';
  const refs = Array.isArray(ev.evidenceRefs) ? ev.evidenceRefs : [];
  if (!refs.some((r) => evidenceResolves(state, r))) return 'evidenceUnresolved';
  if (ev.outcome === 'change-needed') {
    if (!findingsKnown) return 'findingsWithheld';
    if (!findingFor(state, ev, asOfMs)) return 'changeNeededNoFinding';
  }
  return null;
}

/** An obligation's evaluations on or before asOfMs, newest first. */
function evaluationsOf(state, obligationId, asOfMs = Infinity) {
  return list(state, 'obligationEvaluations')
    .filter((ev) => ev && ev.obligationId === obligationId && !Number.isNaN(toMs(ev.evaluatedAt)) && toMs(ev.evaluatedAt) <= asOfMs)
    .sort((a, b) => toMs(b.evaluatedAt) - toMs(a.evaluatedAt) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
}

/**
 * One enabled obligation at a point in time.
 *
 * @returns {{
 *   state: 'first-cycle' | 'current' | 'due' | 'overdue', inFirstCycle: boolean,
 *   months: number, cycleStartMs: number, dueMs: number,
 *   credited: object|null,           the evaluation that counts, if any
 *   latest: object|null,             the newest evaluation, credited or not
 *   latestRefusal: string|null,      why `latest` earns no credit
 * }}
 * `current` — the credited evaluation is within its cycle at `atMs`; `due` —
 * the next evaluation falls due within the lead window; `overdue` — past due;
 * `first-cycle` — inside the first cycle with nothing credited and not yet
 * due. `changePending` — the newest live evaluation is change-needed with no
 * finding: nothing is credited and it is at least `due`. `inFirstCycle` is what CTX-04 excludes, evaluated or not (header).
 */
function obligationCycleState({ state, def, entry, atMs, findingsKnown = true }) {
  const start = cycleStartMs(firstEnabledMs(state, entry));
  const evals = evaluationsOf(state, entry.obligationId, atMs);
  const latest = evals[0] || null;
  const latestRefusal = latest ? creditRefusal(state, latest, atMs, { findingsKnown }) : null;
  // The newest judgement wins: when the latest non-void evaluation called for
  // a change that no finding carries, no older evaluation keeps the obligation
  // "evaluated" — the gap found has no action yet (co-owner diff review,
  // finding 3; TRYGGR_INTENT.md §11, §12).
  const newestLive = evals.find((ev) => !isVoided(ev)) || null;
  // Whatever else refuses it (archived evidence too): a newest change-needed
  // with no linked finding always blocks older credit (co-owner re-check 1).
  const changePending = !!newestLive && newestLive.outcome === 'change-needed' &&
    (!findingsKnown || !findingFor(state, newestLive, atMs));
  const olderCredited = evals.find((ev) => creditRefusal(state, ev, atMs, { findingsKnown }) === null) || null;
  const credited = changePending ? null : olderCredited;
  const months = cycleMonths(def, entry, credited);
  const firstDue = addMonths(start, cycleMonths(def, entry, null));
  const basis = credited || olderCredited;
  const dueMs = basis ? addMonths(toMs(basis.evaluatedAt), cycleMonths(def, entry, basis)) : firstDue;
  const inFirstCycle = atMs < firstDue;
  const lead = Math.min(DUE_SOON_MAX_DAYS * DAY_MS, (dueMs - (basis ? toMs(basis.evaluatedAt) : start)) / 3);
  let st;
  if (atMs > dueMs) st = 'overdue';
  else if (changePending || atMs >= dueMs - lead) st = 'due';
  else st = credited ? 'current' : 'first-cycle';
  return { state: st, inFirstCycle, months, cycleStartMs: start, dueMs, credited, latest, latestRefusal, changePending };
}

/** @returns {{ fields } | { error }} — the caller's part of a new evaluation. */
function validateEvaluationFields(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return { error: 'evaluation object is required' };
  const obligationId = text(input.obligationId);
  if (!obligationId || obligationId.length > MAX_ID) return { error: 'obligationId is required' };
  if (!OUTCOMES.includes(input.outcome)) return { error: `outcome must be one of: ${OUTCOMES.join(', ')}` };
  const note = text(input.note);
  if (!note) return { error: 'note is required: say what the evaluation looked at and what it concluded' };
  if (note.length > MAX_NOTE) return { error: `note must be at most ${MAX_NOTE} characters` };
  if (!Array.isArray(input.evidenceRefs) || !input.evidenceRefs.length) {
    return { error: 'evidenceRefs must name at least one document or artefact the evaluation rests on' };
  }
  if (input.evidenceRefs.length > MAX_EVIDENCE) return { error: `evidenceRefs may name at most ${MAX_EVIDENCE} records` };
  const evidenceRefs = [];
  for (const r of input.evidenceRefs) {
    if (!r || typeof r !== 'object' || !EVIDENCE_TYPES.includes(r.type) || !text(r.id) || text(r.id).length > MAX_ID) {
      return { error: `each evidence reference is { type: ${EVIDENCE_TYPES.join(' | ')}, id }` };
    }
    if (!evidenceRefs.some((x) => x.type === r.type && x.id === text(r.id))) evidenceRefs.push({ type: r.type, id: text(r.id) });
  }
  return { fields: { obligationId, outcome: input.outcome, note, evidenceRefs } };
}

function validateVoidReason(reason) {
  const r = text(reason);
  if (!r) return { error: 'reason is required: say why this evaluation is withdrawn' };
  if (r.length > MAX_VOID_REASON) return { error: `reason must be at most ${MAX_VOID_REASON} characters` };
  return { reason: r };
}

module.exports = {
  OBLIGATION_EVALUATION_FROM,
  CYCLE_MONTHS,
  OUTCOMES,
  EVIDENCE_TYPES,
  DAY_MS,
  toMs,
  addMonths,
  cycleMonths,
  frequencyFor,
  anchorOf,
  anchorsToAdd,
  firstEnabledMs,
  cycleStartMs,
  isVoided,
  findingFor,
  evidenceResolves,
  creditRefusal,
  evaluationsOf,
  obligationCycleState,
  validateEvaluationFields,
  validateVoidReason,
};
